'use client'

import { Loader } from 'fukku/Loader'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { AddToBagButton } from 'product-card/components/shared/ProductSizes/AddToBag/AddToBagButton'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductCardStock } from 'product-card/hooks/useProductCardStock'
import { useProductStock } from 'product/services/productStock/client/useProductStock'
import type { ProductColor } from 'product/types/Product'
import { cx } from 'utils/cx'

import styles from './SizeSheet.module.scss'
import text from 'fukku/styles/texts.module.scss'

interface SizeSheetProps {
	productColors: ProductColor[]
	onClose: () => void
}

export const SizeSheet = ({ productColors, onClose }: SizeSheetProps) => {
	const { t } = useLabels()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { selectedColorId, productId } = useProductCardContext()

	const { data: productStock, isLoading: isStockLoading } = useProductStock({
		productId,
		country: countryISO,
		language: languageISO,
	})
	const { productSizesFullInfo } = useProductCardStock({
		productStock,
		productColors,
	})

	if (isStockLoading) {
		return (
			<Loader
				accessibilityText={{
					loading: t('accessibility.productCard.sizePicker.loading.text'),
					loaded: t('accessibility.productCard.sizePicker.loaded.text'),
				}}
				loading
			/>
		)
	}

	if (productSizesFullInfo) {
		return (
			<div
				data-testid='productCard.sizeSheetContent'
				className={styles.sizeSheetBody}
			>
				{productSizesFullInfo.map((size) => (
					<div
						key={`${size.id}/${selectedColorId}/${productId}`}
						className={styles.sizeSheetAddToBagButtonContainer}
					>
						<AddToBagButton
							className={cx(
								styles.sizeSheetAddToBagButton,
								size.lastUnits && styles.lastUnits
							)}
							size={size}
							onClose={onClose}
						>
							{size.lastUnits && (
								<span
									className={`${styles.lastUnitsText} ${text.bodyMRegular}`}
								>
									{t('pdp.sizeSelector.lastItems.text')}
								</span>
							)}
						</AddToBagButton>
					</div>
				))}
			</div>
		)
	}

	return null
}
